import { SelectionModel } from '@angular/cdk/collections';
import {
	AfterViewInit,
	Component,
	EventEmitter,
	forwardRef,
	Inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { CommonService } from 'src/app/services/common.service';
import { LookupData, SystemicCaseChargeResponse, SystemicChargeVO } from 'src/app/services/gen';
import { NoteVO } from 'src/app/services/gen/model/models';
import { SharedCodeService } from 'src/app/services/shared-code.service';
import { LookUpDataTypes, ScrollStrategy } from 'src/app/shared/common-structures/app.constants';
import { CaseDetailV2 } from 'src/app/shared/common-structures/case-detail';
import { AlertService } from 'src/app/shared/ims-alerts/ims-alerts.service';
import { CaseDetailUtils } from 'src/app/shared/utility/caseDetailUtils';
import { Utils } from 'src/app/shared/utility/utils';
import { NotesDialogComponent } from '../notes-dialog/notes-dialog.component';
import { DashboardService } from '../../dashboard/dashboard.service';
import { SystemicDashboardComponent } from '../../shared-edit-forms/systemic-dashboard/systemic-dashboard.component';
import { LoadingService } from './../../../../services/loading/loading.service';
import { CaseDetailsService } from './../../case-details/case-details.service';
import { SystemicService } from './../../systemic/systemic.service';
import { ExcelService } from 'src/app/services/excel.service';

interface AppSystemicCaseChargeResponse extends SystemicCaseChargeResponse {
	caseDetail: CaseDetailV2;
}

@Component({
	selector: 'app-systemic-detail',
	templateUrl: './systemic-detail.component.html',
	styleUrls: ['./systemic-detail.component.scss'],
})
export class SystemicDetailComponent implements OnInit, AfterViewInit, OnChanges {
	@Input() systemicCaseCharges: AppSystemicCaseChargeResponse[];
	@Input() allCharges: AppSystemicCaseChargeResponse[];
	@Input() isSystemicManager: boolean;
	@Input() showActions: boolean = false;
	@Input() systemicNotes: NoteVO[];
	@Input() selectedCase: any = {};	
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@Input() systemicCaseId: number = 0;
	@Input() isSystemicModule: boolean = false;
	@Output() onSave: EventEmitter<any> = new EventEmitter<any>();
	modified: boolean = false;
	notes: NoteVO[] = [];
	noteTypes: any[] = [];
	adrNotes: NoteVO[] = [];
	hasOFPAdminrole: boolean;
	regularNotes: NoteVO[] = [];
	selectedIndexName: string;
	isAdrRoleHolder: boolean = false;
	userSelectedOfficeCode: string;
	displayColumns: string[] = [];
	defaultColumns: string[] = [
		'ciChargeNumber',
		'rspName',
		'createdOn',
		'alStatuteShortName',
		'alBasisShortName',
		'alIssueShortName',
		'pchpAssessments',
		'ciAcctOfficeDescription',
		'chargeAssignments',
		'coordinationStatus',
	];
	dataSource: MatTableDataSource<AppSystemicCaseChargeResponse> = new MatTableDataSource();
	selection = new SelectionModel<AppSystemicCaseChargeResponse>(true, []);
	filters: any = {};
	coordinationStatus: LookupData[] = [];
	ariaResultAlert: number;
	public getDescriptionByValue = Utils.getDescriptionByValue;

	constructor(
		private router: Router,
		private sharedCodeServ: SharedCodeService,
		private alert: AlertService,
		private loader: LoadingService,
		public dialog: MatDialog,
		@Inject(forwardRef(() => SystemicDashboardComponent)) public parent: SystemicDashboardComponent,
		private caseDetailService: CaseDetailsService,
		private systemicServ: SystemicService,
		public cmnServ: CommonService,
		private dashboardServ: DashboardService,
		private excelService: ExcelService
	) {}

	ngOnInit(): void {
		this.setupMatTable();
		this.getCoordinationStatus();
		this.systemicServ.onExport.subscribe((params) => {
			this.download(params.format, params.fileName, params.sheetName);
		});
	}

	ngOnChanges(changes): void {
		if (this.dataSource && this.systemicCaseCharges) {
			this.dataSource.data = this.systemicCaseCharges || [];
			this.dataSource._updateChangeSubscription();
		}
		if (this.isSystemicModule && this.isSystemicManager) {
			this.setColumns();
		}
		if (changes.selectedCase && this.selectedCase) {
			this.setColumns();
		}
	}

	setColumns() {
		this.displayColumns = [...this.defaultColumns].filter(
			(x) => this.selectedCase?.status?.code != 'IN' || x != 'coordinationStatus'
		);
		if (this.isSystemicModule && this.isSystemicManager && this.showActions) {
			if (!this.displayColumns.includes('select')) {
				this.displayColumns.unshift('select');
				this.displayColumns.push('actions');
			}
		}
	}

	/**
	 * @description setup filter logic
	 * instead of sending pairs like "column: filterValue", now filters is an object that holds multiple key-value pairs.
	 */
	ngAfterViewInit() {
		this.dataSource.filterPredicate = (data: any, filters) => {
			const rs = this.displayColumns.map((item) => {
				if (Utils.isNullCondition(filters[item]) || _.isEmpty(filters[item])) {
					return true;
				}
				let rowValue = data[item] ? data[item] : '';
				if (item === 'ciChargeNumber') rowValue = data.caseDetail?.ciChargeNumber;
				if (item === 'rspName') rowValue = data.caseDetail?.rspName;
				if (item === 'alStatuteShortName') rowValue = this.displayAllegations(data.caseDetail, 'alStatuteShortName');
				if (item === 'alBasisShortName') rowValue = this.displayAllegations(data.caseDetail, 'alBasisShortName');
				if (item === 'alIssueShortName') rowValue = this.displayAllegations(data.caseDetail, 'alIssueShortName');
				if (item === 'pchpAssessments') rowValue = this.displayPCHPAssessments(data.caseDetail);
				if (item === 'ciAcctOfficeDescription') rowValue = data.caseDetail?.ciAcctOfficeDescription;
				if (item === 'chargeAssignments') rowValue = this.displayAssignedTo(data.caseDetail);
				if (item === 'coordinationStatus')
					rowValue = this.coordinationStatus?.find((el) => el.code === data.coordinationStatusCode)?.description;

				let filterValue = filters[item];
				return rowValue?.toLowerCase()?.includes(filterValue?.toLowerCase());
			});
			return rs.every(Boolean);
		};
	}

	/**
	 * @description Setup matTable. Including dataSource initialization, binding of paginator and sorter,
	 * and setup default sort.
	 */
	setupMatTable() {
		//	this.dataSource = new MatTableDataSource();
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		// setup default sort
		const sortState: Sort = { active: 'coordinationStatus', direction: 'desc' };
		this.sort.active = sortState.active;
		this.sort.direction = sortState.direction;
	}

	openNotesDialog() {
		this.notes = this.systemicNotes;
		const data = {
			title: 'Notes',
			caseId: this.systemicCaseId,
			systemicCase: true,
		};
		const dialogRef = this.dialog.open(NotesDialogComponent, {
			minHeight: '60vh',
			height: 'auto',
			width: '80%',
			data: data,
			panelClass: 'app-dialog-container-full-height',
			disableClose: false,
			scrollStrategy: ScrollStrategy,
		});
		dialogRef.componentInstance.notes = this.notes || [];
		dialogRef.componentInstance.isAdr = this.isAdrRoleHolder && this.selectedIndexName === 'ADR';
		dialogRef.componentInstance.subjectOptions = this.noteTypes;

		dialogRef.componentInstance.updateLabels();
		dialogRef.componentInstance.setPageData();
		const subscription = dialogRef.componentInstance.onUpdate.subscribe((res) => {
			this.notes = res;
		});
		const subjectOptionSub = dialogRef.componentInstance.subjectChange.subscribe((res) => {
			this.noteTypes = res;
		});
		dialogRef.afterClosed().subscribe((res) => {
			if (res) {
				subscription.unsubscribe();
				subjectOptionSub.unsubscribe();
			}
		});
	}

	getCoordinationStatus() {
		this.sharedCodeServ.getSharedCodes(LookUpDataTypes.SYSTEMIC_COORDINATION_STATUS).subscribe(
			(res) => {
				this.coordinationStatus = res;
			},
			(err) => {
				this.alert.error('Error happened while getting shared codes. Please contact system administrator.',err);
			}
		);
	}

	/**
	 * @description filter content on one column
	 * @param filterValue filter query
	 * @param column column to filter on
	 * @todo put this function in a reusable location
	 */
	applyColumnFilter(filterValue: string, column: string) {
		this.filters[column] = filterValue;
		this.dataSource.filter = this.filters;
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}

		// For aria alert when user uses filter
		this.ariaResultAlert = this.dataSource.filteredData.length;
	}

	/**
	 * @description controls the master toggle.
	 * On select: select every row.
	 * On unselect: clear selection.
	 * @param event
	 */
	masterToggle(event) {
		if (event.checked) this.dataSource.filteredData.forEach((a) => this.selection.select(a));
		else this.selection.clear();
	}

	public displayAllegations = CaseDetailUtils.displayAllegations;

	public displayPCHPAssessments = CaseDetailUtils.displayPCHPAssessments;

	public displayAssignedTo = CaseDetailUtils.displayAssignedTo;

	removeFromSystemic(row) {
		const payload = [row.ciChargeInquiryId || row.caseId];
		this.parent.confirmRemoveFromSystemic(payload);
	}
	removeAll() {
		this.parent.confirmRemoveFromSystemic(this.selection.selected.map((item) => item.caseId));
	}

	download(format, fileName?, sheetName?) {
		let tableArray= [];
		this.dataSource.data.forEach((row) => {
			tableArray.push({
				'EEOC Number': row.caseDetail?.ciChargeNumber,
				'Respondent': row.caseDetail?.rspName,
				'Receiving Office Intent': this.cmnServ.displayEsttoLocale(row.createdOn),
				'Statute': this.displayAllegations(row.caseDetail, 'alStatuteShortName'),
				'Basis': this.displayAllegations(row.caseDetail, 'alBasisShortName'),
				'Issue': this.displayAllegations(row.caseDetail, 'alIssueShortName'),
				'PCHP': this.displayPCHPAssessments(row.caseDetail),
				'Accountable Office': row.caseDetail?.ciAcctOfficeDescription,
				'Assigned to': this.displayAssignedTo(row.caseDetail)
			});
		});
		this.excelService.exportToExcel(tableArray, fileName, sheetName);
	}
	/**
	 * @description when user click on any case sets the caseinformation in common service.
	 * @param caseInformation information of selected case
	 */
	public caseSelected(caseInformation: CaseDetailV2) {
		if (Utils.notNullCondition(caseInformation)) {
			this.dashboardServ.setCaseInformation(caseInformation);
			this.router.navigateByUrl('/case-details/' + caseInformation.ciChargeInquiryId);
		}
	}

	save() {
		let rows: Array<SystemicChargeVO> = [];
		this.allCharges.forEach((row: any) => {
			rows.push({
				chargeId: row.caseId,
				coordinationStatus: row.coordinationStatusCode
					? this.coordinationStatus.find((x) => x.code == row.coordinationStatusCode)
					: null,
			});
		});
		this.loader.show();
		this.caseDetailService.addSystemicCaseToCharges(this.systemicCaseId, rows).subscribe(
			(res) => {
				this.loader.hide();
				this.alert.success('Status has been saved successfully');
				this.onSave.emit(true);
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) {
					this.alert.error(error?.error , error);
				} else {
					this.alert.error('error occurred while saving information',error);
				}
			}
		);
	}
}
